import { useState, useRef, useCallback } from 'react'

/**
 * Interactive before/after image comparator. Users drag the handle
 * (mouse, touch, keyboard) to reveal more "after" vs "before".
 *
 * Custom impl (no library) — pure CSS clip-path + pointer events.
 * Bundle impact: ~1KB vs 8KB for react-compare-slider.
 *
 * Accessibility:
 *   - Slider has role="slider" + aria-valuenow
 *   - Keyboard: Arrow Left/Right = ±5%, Home/End = 0/100
 */
interface BeforeAfterSliderProps {
  beforeImage: string
  afterImage: string
  beforeLabel?: string
  afterLabel?: string
  alt: string
  className?: string
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  beforeLabel = 'Avant',
  afterLabel = 'Après',
  alt,
  className = '',
}: BeforeAfterSliderProps) {
  const [pos, setPos] = useState(50)
  const [dragging, setDragging] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  const updateFromClientX = useCallback((clientX: number) => {
    const rect = rootRef.current?.getBoundingClientRect()
    if (!rect) return
    const next = ((clientX - rect.left) / rect.width) * 100
    setPos(Math.min(100, Math.max(0, next)))
  }, [])

  const onMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setDragging(true)
    updateFromClientX(e.clientX)
  }

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return
    updateFromClientX(e.clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches[0]) updateFromClientX(e.touches[0].clientX)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 5))
    else if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 5))
    else if (e.key === 'Home') setPos(0)
    else if (e.key === 'End') setPos(100)
  }

  return (
    <div
      ref={rootRef}
      className={`relative select-none overflow-hidden rounded-2xl bg-slate-100 ${className}`}
      style={{ aspectRatio: '4 / 3', cursor: dragging ? 'ew-resize' : 'grab' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={() => setDragging(false)}
      onMouseLeave={() => setDragging(false)}
      onTouchStart={(e) => {
        setDragging(true)
        if (e.touches[0]) updateFromClientX(e.touches[0].clientX)
      }}
      onTouchMove={onTouchMove}
      onTouchEnd={() => setDragging(false)}
    >
      {/* Base layer: AFTER (full size) */}
      <img
        src={afterImage}
        alt={`${afterLabel} — ${alt}`}
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />

      {/* Overlay: BEFORE, clipped from left */}
      <img
        src={beforeImage}
        alt={`${beforeLabel} — ${alt}`}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        draggable={false}
      />

      {/* Labels */}
      <div className="absolute left-3 top-3 rounded-md bg-black/70 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur">
        {beforeLabel}
      </div>
      <div className="absolute right-3 top-3 rounded-md bg-black/70 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur">
        {afterLabel}
      </div>

      {/* Handle line + grip */}
      <div
        role="slider"
        aria-label={`Comparaison ${beforeLabel} / ${afterLabel}`}
        aria-valuenow={Math.round(pos)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="absolute inset-y-0 w-0.5 cursor-ew-resize bg-white shadow-[0_0_12px_rgba(0,0,0,0.6)] focus:outline-none"
        style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg ring-4 ring-black/10">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="15 6 9 12 15 18" />
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </div>
      </div>

      {/* Hint text (first use) */}
      <div className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-[11px] font-medium text-white drop-shadow-md">
        Glissez pour comparer →
      </div>
    </div>
  )
}
